import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'

import {
  Button,
  useImperativeInterval,
  useLocalStorage,
  useContentRefreshDomains,
} from '@pepper/ui'

import { fetchGoogleOauthResponse } from '@pepper/utils/src/api/integrations/google'

import s from './GoogleIntegrationButton.module.css'

type Props = {
  companyId: string
  type?: 'link' | 'ghost' | 'primary'
  onError?: (error: string) => void
  onSuccess?: () => void
  cta?: string
  size?: 'small' | 'middle' | 'large'
}

const USER_GOOGLE_AUTH_CHECK_INTERVAL_TIME = 1000

export const GoogleIntegrationButton: FC<Props> = ({
  type = 'primary',
  onError,
  companyId,
  cta,
  size = 'middle',
  onSuccess,
}) => {
  const iconSrc =
    'https://assets.peppercontent.io/icons/social-media/google.svg'

  const [_, setCompanyIdInLocalStorage] = useLocalStorage(
    'companyId',
    companyId
  )
  const [__, setLsDomainPropertySelectorModal] = useLocalStorage(
    'domain_property_selector',
    ''
  )
  const queryClient = useQueryClient()
  const { refetch: refetchContentRefreshData } =
    useContentRefreshDomains(companyId)
  const [canStopAuthCheckInterval, setCanStopAuthCheckInterval] =
    useState(false)
  const [isFetchingRedirectUrl, setIsFetchingRedirectUrl] = useState(false)

  const newWindowProxy = useRef<null | WindowProxy>()
  const { set: startAuthSuccessCheck, clear: stopAuthSuccessCheck } =
    useImperativeInterval(() => {
      if (newWindowProxy.current?.closed) {
        setCanStopAuthCheckInterval(true)
        queryClient.invalidateQueries(['profile-integrations', companyId], {
          refetchType: 'all',
        })
        refetchContentRefreshData()
      }
    }, USER_GOOGLE_AUTH_CHECK_INTERVAL_TIME)

  useEffect(() => {
    if (canStopAuthCheckInterval) {
      stopAuthSuccessCheck()
      setIsFetchingRedirectUrl(false)
      setCanStopAuthCheckInterval(false)
    }
  }, [canStopAuthCheckInterval, stopAuthSuccessCheck])

  const handleConnectAccount = useCallback(async () => {
    setLsDomainPropertySelectorModal('true')
    setIsFetchingRedirectUrl(true)
    const response = await fetchGoogleOauthResponse({
      companyId,
    })
    if (response.status === 'error') {
      onError?.(response.error)
      return
    }
    setCompanyIdInLocalStorage(companyId)
    newWindowProxy.current = window.open(
      response.data.redirect_url,
      'googleOauthWindow'
    )
    startAuthSuccessCheck()
    onSuccess?.()
  }, [companyId, setCompanyIdInLocalStorage, onError, startAuthSuccessCheck])

  return (
    <Button
      onClick={handleConnectAccount}
      type={type}
      loading={isFetchingRedirectUrl}
      size={size}
      icon={
        type === 'link' ? (
          <Icon icon="lucide:plus-circle" />
        ) : (
          iconSrc && <img className={s.icon} alt="google" src={iconSrc} />
        )
      }
      data-id="add-domain-connect-google-button"
    >
      <span className={s.cta}> {cta ?? 'Connect Google Account'}</span>
    </Button>
  )
}
